import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthProvider';
import { FaEdit, FaArrowLeft, FaDownload } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

// Import the corresponding CSS file
import '../assets/css/CaseDetails.css';

const CaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

  console.log("id", id)

  const [caseDetails, setCaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Stop if there is no ID in the URL
    if (!id) {
      setError("No Case ID provided.");
      setLoading(false);
      return;
    }

    // Stop if the user is not logged in
    if (!user?.token) {
      setError("You must be logged in to view case details.");
      toast.error("Please log in first.");
      navigate('/login');
      setLoading(false);
      return;
    }

    const fetchCaseDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/api/cases/${id}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        if (response.data?.data?.case) {
          setCaseDetails(response.data.data.case);
        } else {
          throw new Error("Case data not found in the API response.");
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Failed to fetch case details.";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseDetails();
  }, [id, BASE_URL, user, navigate]);


  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render a loading screen while fetching data
  if (loading) {
    return (
      <Layout>
        <div className="status-screen">
          <p>Loading Case Details...</p>
        </div>
      </Layout>
    );
  }

  // Render an error message if something went wrong
  if (error) {
    return (
      <Layout>
        <div className="status-screen">
          <div className="error-card">
            <h2>Error Fetching Case</h2>
            <p>{error}</p>
            <Link to="/cases" className="btn btn-back">
              <FaArrowLeft className="icon" />
              Back to Case List
            </Link>
          </div>
        </div>
      </Layout>
    );
  }
  
  // Render if the case is not found after loading
  if (!caseDetails) {
     return (
      <Layout>
        <div className="status-screen">
           <div className="error-card">
             <h1>Case Not Found</h1>
             <p>The case you are looking for does not exist or could not be loaded.</p>
             <Link to="/cases" className="btn btn-back">
               <FaArrowLeft className="icon" />
               Back to Case List
             </Link>
           </div>
        </div>
      </Layout>
    );
  }

  // Render the full case details
  return (
    <Layout>
      <div className="page-wrapper">
        <div className="container">
          <header className="page-header">
            <h1 className="page-title">Case Details</h1>
            <div className="header-actions">
              {hasPermission('edit_cases') && (
                <Link
                  to={`/cases/edit/${caseDetails._id}`}
                  className="btn btn-edit"
                >
                  <FaEdit className="icon" />
                  Edit Case
                </Link>
              )}
              <Link to="/cases" className="btn btn-back">
                <FaArrowLeft className="icon" />
                Back to List
              </Link>
            </div>
          </header>

          <div className="card">
            <h2 className="card-header-title">
              Case: <span className="highlight-text">{caseDetails.caseNumber}</span>
            </h2>
            <div className="card-grid">
              <div className="info-block">
                <p>Case Type</p>
                <p>{caseDetails.caseType}</p>
              </div>
              <div className="info-block">
                <p>Year</p>
                <p>{caseDetails.year}</p>
              </div>
              <div className="info-block">
                <p>Grouping</p>
                <p>{caseDetails.grouping}</p>
              </div>
            </div>
          </div>

          <div className="details-grid">
            <div className="card details-card">
              <h3>Petitioner's Details</h3>
              <p><strong>Name:</strong> {caseDetails.petitionerName}</p>
              <p><strong>Advocate:</strong> {caseDetails.petitionerAdvocate}</p>
              <p><strong>Address:</strong> {caseDetails.petitionerAddress}</p>
            </div>
            <div className="card details-card">
              <h3>Respondent's Details</h3>
              <p><strong>Name:</strong> {caseDetails.respondentName}</p>
              <p><strong>Advocate:</strong> {caseDetails.respondentAdvocate}</p>
              <p><strong>Address:</strong> {caseDetails.respondentAddress}</p>
            </div>
          </div>

          <div className="card">
            <h3 className="card-header-title">Case Documents</h3>
            {caseDetails.files && caseDetails.files.length > 0 ? (
              <ul className="file-list">
                {caseDetails.files.map((file) => (
                  <li key={file._id} className="file-item">
                    <div>
                      <p className="file-name">{file.filename}</p>
                      <p className="file-size">
                        Size: {formatFileSize(file.size)}
                      </p>
                    </div>
                    <a
                      href={`${BASE_URL}/api/cases/${caseDetails._id}/files/${file._id}?token=${user.token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-download"
                      download={file.filename}
                    >
                      <FaDownload className="icon" />
                      Download
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No documents found for this case.</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CaseDetails;